/**
 * Permission Gate Component
 *
 * Provides declarative permission checking for UI components.
 * Handles both feature permissions and legacy role-based access.
 */

import React, { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  hasAnyFeaturePermission,
  hasAllFeaturePermissions,
} from '../../utils/permissionUtils';
import { FeaturePermission } from '../../constants/featurePermissions';
import { Lock } from 'lucide-react';
import UpgradeModal from './UpgradeModal';

// Base permission gate props
interface BasePermissionGateProps {
  children: ReactNode;
  fallback?: ReactNode;
  loading?: ReactNode;
  onAccessDenied?: () => void;
  showLock?: boolean; // Show lock icon when subscription limits are reached
  dribble?: boolean; // If true, show animated lock on top-right; if false, show static lock on right-center
}

// Feature permission gate props
interface FeaturePermissionGateProps extends BasePermissionGateProps {
  requiredFeatures: FeaturePermission | FeaturePermission[];
  requireAll?: boolean;
  roles?: never; // Exclude role-based props when using features
}

// Role-based permission gate props
interface RolePermissionGateProps extends BasePermissionGateProps {
  roles: string | string[];
  requiredFeatures?: never; // Exclude feature props when using roles
  requireAll?: never;
}

// Custom permission gate props
interface CustomPermissionGateProps extends BasePermissionGateProps {
  condition: (user: any, companyUser: any) => boolean;
  requiredFeatures?: never;
  roles?: never;
  requireAll?: never;
}

type PermissionGateProps =
  | FeaturePermissionGateProps
  | RolePermissionGateProps
  | CustomPermissionGateProps;

/**
 * Main Permission Gate Component
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  fallback = null,
  loading = null,
  onAccessDenied,
  showLock = false,
  dribble = false,
  ...props
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentCompanyUser } = useSelector((state: RootState) => state.auth);
  const { currentCompany } = useSelector((state: RootState) => state.companies);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [restrictionInfo, setRestrictionInfo] = useState<{
    featureName?: string;
    limitType?: 'properties' | 'users' | 'rentalUnits' | 'feature';
    currentLimit?: number;
    currentUsage?: number;
  }>({});

  // Show loading state if user data is not yet available
  if (!user) {
    return loading ? <>{loading}</> : null;
  }
  if(user.role=='super_admin'){
    return <>{children}</>;
  }

  let hasUserAccess = false;
  let hasCompanyAccess = false;
  let requiredFeaturesList: string[] = [];

  // Feature-based permission checking
  if ('requiredFeatures' in props && props.requiredFeatures) {
    const features = Array.isArray(props.requiredFeatures)
      ? props.requiredFeatures
      : [props.requiredFeatures];

    requiredFeaturesList = features;
    const userPermissions = (currentCompanyUser?.featurePermissions as FeaturePermission[]) || [];

    if (userPermissions.length > 0) {
      // Check user permissions
      if (props.requireAll) {
        hasUserAccess = hasAllFeaturePermissions(userPermissions, features);
      } else {
        hasUserAccess = hasAnyFeaturePermission(userPermissions, features);
      }

      // Check company features list if user has access
      if (hasUserAccess && currentCompany?.featuresList) {
        const companyFeatures = currentCompany.featuresList as string[];
        if (props.requireAll) {
          hasCompanyAccess = features.every(feature => companyFeatures.includes(feature));
        } else {
          hasCompanyAccess = features.some(feature => companyFeatures.includes(feature));
        }
      } else if (hasUserAccess && !currentCompany?.featuresList) {
        // If no company features list, assume user has access
        hasCompanyAccess = true;
      }
    }
  }
  // Role-based permission checking
  else if ('roles' in props && props.roles) {
    const allowedRoles = Array.isArray(props.roles) ? props.roles : [props.roles];
    hasUserAccess = allowedRoles.includes(user.role);
    hasCompanyAccess = hasUserAccess; // Role-based doesn't need company check
  }
  // Custom permission logic
  else if ('condition' in props) {
    hasUserAccess = props.condition(user, currentCompanyUser);
    hasCompanyAccess = hasUserAccess; // Custom doesn't need company check
  }

  // Handle restriction info setup
  const setupRestrictionInfo = () => {
    if (requiredFeaturesList.length > 0) {
      const firstFeature = requiredFeaturesList[0];
      let limitType: 'properties' | 'users' | 'rentalUnits' | 'feature' = 'feature';
      let featureName = firstFeature;

      // Determine limit type based on feature name
      if (firstFeature.includes('property') || firstFeature.includes('properties')) {
        limitType = 'properties';
        featureName = 'Add Properties';
        setRestrictionInfo({
          featureName,
          limitType,
          currentLimit: currentCompany?.limitations?.maxPropertiesPerCompany,
          currentUsage: currentCompany?.usage?.properties
        });
      } else if (firstFeature.includes('user') || firstFeature.includes('users')) {
        limitType = 'users';
        featureName = 'Add Users';
        setRestrictionInfo({
          featureName,
          limitType,
          currentLimit: currentCompany?.limitations?.maxUsersPerCompany,
          currentUsage: currentCompany?.usage?.users
        });
      } else if (firstFeature.includes('rentalunit') || firstFeature.includes('rental')) {
        limitType = 'rentalUnits';
        featureName = 'Add Rental Units';
        setRestrictionInfo({
          featureName,
          limitType,
          currentLimit: currentCompany?.limitations?.maxRentalUnitsPerProperty,
          currentUsage: currentCompany?.usage?.rentalUnits
        });
      } else {
        setRestrictionInfo({ featureName, limitType });
      }
    }
  };

  // User has permission but company subscription doesn't allow it
  if (hasUserAccess && !hasCompanyAccess && showLock) {
    // Clone the child element and add disabled styles
    const disabledChild = React.isValidElement(children)
      ? React.cloneElement(children as React.ReactElement<any>, {
          className: `${(children as React.ReactElement<any>).props.className || ''} opacity-50 pointer-events-none`,
          disabled: true,
        })
      : children;

    return (
      <>
        <div
          className="relative inline-flex items-center cursor-pointer group"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setupRestrictionInfo();
            setShowUpgradeModal(true);
          }}
        >
          {disabledChild}

          {/* Dribble mode: Animated lock on top-right */}
          {dribble ? (
            <div className="absolute -top-1 -right-1 z-10">
              <div className="relative">
                <Lock className="h-4 w-4 text-red-500 bg-white dark:bg-gray-800 rounded-full p-0.5" />
                <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-75"></div>
              </div>
            </div>
          ) : (
            /* Non-dribble mode: Static lock on right-center */
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10">
              <Lock className="h-4 w-4 text-red-500 bg-white dark:bg-gray-800 rounded-full p-0.5" />
            </div>
          )}

          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
            Upgrade required
          </div>
        </div>
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          {...restrictionInfo}
        />
      </>
    );
  }

  // User has permission but company subscription doesn't allow it (without lock icon)
  if (hasUserAccess && !hasCompanyAccess && !showLock) {
    if (onAccessDenied) {
      onAccessDenied();
    }
    return fallback ? <>{fallback}</> : null;
  }

  // Handle access completely denied
  if (!hasUserAccess || !hasCompanyAccess) {
    if (onAccessDenied) {
      onAccessDenied();
    }
    return fallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
};

/**
 * Feature Permission Gate (shorthand for feature-based permissions)
 */
export const FeatureGate: React.FC<{
  features: FeaturePermission | FeaturePermission[];
  requireAll?: boolean;
  children: ReactNode;
  fallback?: ReactNode;
  showLock?: boolean;
  dribble?: boolean;
}> = ({ features, requireAll = false, children, fallback = null, showLock = false, dribble = false }) => {
  return (
    <PermissionGate
      requiredFeatures={features}
      requireAll={requireAll}
      fallback={fallback}
      showLock={showLock}
      dribble={dribble}
    >
      {children}
    </PermissionGate>
  );
};

/**
 * Role Gate (shorthand for role-based permissions)
 */
export const RoleGate: React.FC<{
  roles: string | string[];
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ roles, children, fallback = null }) => {
  return (
    <PermissionGate roles={roles} fallback={fallback}>
      {children}
    </PermissionGate>
  );
};

/**
 * Admin Gate (shorthand for admin roles)
 */
export const AdminGate: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback = null }) => {
  return (
    <FeatureGate
      features={['admin_read', 'admin_write', 'company_write', 'users_write']}
      requireAll={false}
      fallback={fallback}
    >
      {children}
    </FeatureGate>
  );
};

/**
 * Write Permission Gate (checks for write/create access to features)
 */
export const WriteGate: React.FC<{
  category: string; // e.g., 'properties', 'tenants', 'payments'
  children: ReactNode;
  fallback?: ReactNode;
  showLock?: boolean;
  dribble?: boolean;
}> = ({ category, children, fallback = null, showLock = false, dribble = false }) => {
  const writeFeature = `${category}_write` as FeaturePermission;

  return (
    <FeatureGate features={writeFeature} fallback={fallback} showLock={showLock} dribble={dribble}>
      <>
        {children}
      </>
    </FeatureGate>
  );
};

/**
 * Read Permission Gate (checks for read access to features)
 */
export const ReadGate: React.FC<{
  category: string; // e.g., 'properties', 'tenants', 'payments'
  children: ReactNode;
  fallback?: ReactNode;
  showLock?: boolean;
  dribble?: boolean;
}> = ({ category, children, fallback = null, showLock = false, dribble = false }) => {
  const readFeature = `${category}_read` as FeaturePermission;

  return (
    <FeatureGate features={readFeature} fallback={fallback} showLock={showLock} dribble={dribble}>
      {children}
    </FeatureGate>
  );
};

/**
 * Update Permission Gate (checks for update access to features)
 */
export const UpdateGate: React.FC<{
  category: string; // e.g., 'properties', 'tenants', 'payments'
  children: ReactNode;
  fallback?: ReactNode;
  showLock?: boolean;
  dribble?: boolean;
}> = ({ category, children, fallback = null, showLock = false, dribble = false }) => {
  const updateFeature = `${category}_update` as FeaturePermission;

  return (
    <FeatureGate features={updateFeature} fallback={fallback} showLock={showLock} dribble={dribble}>
      {children}
    </FeatureGate>
  );
};

/**
 * Delete Permission Gate (checks for delete access to features)
 */
export const DeleteGate: React.FC<{
  category: string; // e.g., 'properties', 'tenants', 'payments'
  children: ReactNode;
  fallback?: ReactNode;
  showLock?: boolean;
  dribble?: boolean;
}> = ({ category, children, fallback = null, showLock = false, dribble = false }) => {
  const deleteFeature = `${category}_delete` as FeaturePermission;

  return (
    <FeatureGate features={deleteFeature} fallback={fallback} showLock={showLock} dribble={dribble}>
      {children}
    </FeatureGate>
  );
};

/**
 * CRUD Permission Gate (checks for specific CRUD operations)
 */
export const CrudGate: React.FC<{
  category: string; // e.g., 'properties', 'tenants', 'payments'
  operation: 'create' | 'read' | 'update' | 'delete';
  children: ReactNode;
  fallback?: ReactNode;
  showLock?: boolean;
  dribble?: boolean;
}> = ({ category, operation, children, fallback = null, showLock = false, dribble = false }) => {
  const operationMap = {
    create: 'write',
    read: 'read',
    update: 'update',
    delete: 'delete'
  };

  const feature = `${category}_${operationMap[operation]}` as FeaturePermission;

  return (
    <FeatureGate features={feature} fallback={fallback} showLock={showLock} dribble={dribble}>
      {children}
    </FeatureGate>
  );
};

/**
 * Any CRUD Permission Gate (checks if user has ANY CRUD permission for a category)
 */
export const AnyCrudGate: React.FC<{
  category: string; // e.g., 'properties', 'tenants', 'payments'
  children: ReactNode;
  fallback?: ReactNode;
  showLock?: boolean;
  dribble?: boolean;
}> = ({ category, children, fallback = null, showLock = false, dribble = false }) => {
  const features: FeaturePermission[] = [
    `${category}_read` as FeaturePermission,
    `${category}_write` as FeaturePermission,
    `${category}_update` as FeaturePermission,
    `${category}_delete` as FeaturePermission
  ];

  return (
    <FeatureGate
      features={features}
      requireAll={false}
      fallback={fallback}
      showLock={showLock}
      dribble={dribble}
    >
      {children}
    </FeatureGate>
  );
};

/**
 * Permission-aware Button Component
 */
export const PermissionButton: React.FC<{
  features?: FeaturePermission | FeaturePermission[];
  roles?: string | string[];
  requireAll?: boolean;
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  disabledMessage?: string;
}> = ({
  features,
  roles,
  requireAll = false,
  children,
  onClick,
  className = '',
  disabled = false,
  disabledMessage = 'Access denied'
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentCompanyUser } = useSelector((state: RootState) => state.auth);

  let hasPermission = true;

  if (features) {
    const userPermissions = (currentCompanyUser?.featurePermissions as FeaturePermission[]) || [];
    const featureArray = Array.isArray(features) ? features : [features];

    if (requireAll) {
      hasPermission = hasAllFeaturePermissions(userPermissions, featureArray);
    } else {
      hasPermission = hasAnyFeaturePermission(userPermissions, featureArray);
    }
  } else if (roles) {
    const roleArray = Array.isArray(roles) ? roles : [roles];
    hasPermission = user ? roleArray.includes(user.role) : false;
  }

  const isDisabled = disabled || !hasPermission;

  return (
    <button
      onClick={hasPermission ? onClick : undefined}
      disabled={isDisabled}
      className={`${className} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      title={!hasPermission ? disabledMessage : undefined}
    >
      {children}
    </button>
  );
};

/**
 * Permission-aware Link Component
 */
export const PermissionLink: React.FC<{
  to: string;
  features?: FeaturePermission | FeaturePermission[];
  roles?: string | string[];
  requireAll?: boolean;
  children: ReactNode;
  className?: string;
  fallback?: ReactNode;
}> = ({
  to,
  features,
  roles,
  requireAll = false,
  children,
  className = '',
  fallback = null
}) => {
  const gateProps: any = {};

  if (features) {
    gateProps.requiredFeatures = features;
    gateProps.requireAll = requireAll;
  } else if (roles) {
    gateProps.roles = roles;
  }

  return (
    <PermissionGate {...gateProps} fallback={fallback}>
      <a href={to} className={className}>
        {children}
      </a>
    </PermissionGate>
  );
};

export default PermissionGate;